import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Obst- & Gemüseautomat kaufen“ (/obstautomat).
 *
 * Bündelt bewusst obst + gemüse auf EINER Seite (statt zwei dünner Doorway-
 * Seiten). Eigener inhaltlicher Winkel: erntefrische Direktvermarktung ab
 * Feld/Hof, saisonaler Sortimentswechsel und die EHRLICHE Eingrenzung auf
 * ungekühlte Ware (Kartoffeln, Äpfel, Zwiebeln, Kürbis …) – kein Klon der
 * übrigen Cluster-Seiten. Referenz: Obstbau Nüberlin (echter Betreiber, Live-
 * Link) + Hof-Zink-Video.
 */
@Component({
  selector: 'app-obstautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './obstautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class ObstautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'wege-der-landwirtschaftlichen-direktvermarktung';

  /** Öffentliche Live-Box: Obstbau Nüberlin (echter Betreiber). */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/obstbau-nueberlin/U-MB-00280'
  );

  /** YouTube-Facade (lädt erst nach Klick): Hofbesuch Obsthof Zink. */
  readonly videoThumb = 'https://i.ytimg.com/vi/1gQUYbA9yXs/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@obst.faq1.q:Welches Obst und Gemüse eignet sich ohne Kühlung?`,
      a: $localize`:@@obst.faq1.a:Die marktbox ist ein ungekühlter Automat. Ideal sind lagerfähige Erzeugnisse wie Kartoffeln, Äpfel, Birnen, Zwiebeln, Kürbis, Nüsse oder eingekochte Produkte. Kühlpflichtige Ware ist derzeit nicht vorgesehen.`,
    },
    {
      q: $localize`:@@obst.faq2.q:Kann ich das Sortiment saisonal wechseln?`,
      a: $localize`:@@obst.faq2.a:Ja. Die Fächer lassen sich frei bestücken – von Erdbeeren und Spargel im Frühjahr über Äpfel im Herbst bis zu Kartoffeln und Kürbis. Du passt das Angebot jederzeit an die Ernte an.`,
    },
    {
      q: $localize`:@@obst.faq3.q:Wo stelle ich einen Obstautomaten am besten auf?`,
      a: $localize`:@@obst.faq3.a:Direkt am Feldrand, am Hoftor oder an einer gut befahrenen Straße mit Parkmöglichkeit. Wichtig sind Sichtbarkeit und einfache Erreichbarkeit – die marktbox ist wetterfest und outdoor-tauglich.`,
    },
    {
      q: $localize`:@@obst.faq4.q:Obstautomat kaufen oder mieten?`,
      a: $localize`:@@obst.faq4.a:Beides ist möglich: ab 6.800 € kaufen oder ab 300 € pro Monat mieten. Mieten senkt die Einstiegshürde für den Start, Kaufen lohnt sich im Dauerbetrieb.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/1gQUYbA9yXs?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@obst.metaTitle:Obst- & Gemüseautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@obst.metaDesc:Obst- & Gemüseautomat kaufen oder mieten: erntefrisches Obst & Gemüse 24/7 ab Hof verkaufen. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@obst.metaKeywords:Obstautomat, Gemüseautomat, Apfelautomat, Erdbeerautomat, Spargelautomat, Obst verkaufen Automat, Gemüse verkaufen Automat, Kartoffelautomat, Direktvermarktung Feld, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('obstautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Obst- & Gemüseautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('obstautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('obstautomat-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Zu Gast auf dem Obsthof Zink',
      description:
        'Hofbesuch beim Obsthof Zink: Wie regionale Produkte – Beeren, Äpfel, Steinobst und Eier – über die marktbox rund um die Uhr direkt vermarktet werden.',
      thumbnailUrl: 'https://i.ytimg.com/vi/1gQUYbA9yXs/hqdefault.jpg',
      uploadDate: '2024-09-21',
      duration: 'PT4M52S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/1gQUYbA9yXs',
      contentUrl: 'https://www.youtube.com/watch?v=1gQUYbA9yXs',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/obstautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
